import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, getLocalizedValue } from '../lib/cms';
import usePageMetadata from '../hooks/usePageMetadata';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export default function ServiceERP() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('erp-lead');
        setPageData(page);
      } catch (err) {
        console.error('Error loading ERP Lead page:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Set page metadata
  const title = pageData ? getLocalizedValue(pageData.meta_title, language) : 'ERP Lead & Dynamics 365 Specialist - Insightly ApS';
  const description = pageData ? getLocalizedValue(pageData.meta_description, language) : 'Få struktur, governance og sikker levering på dine ERP-initiativer med René Vilsgaard som din senior freelance ERP Lead.';
  usePageMetadata(title, description);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-offwhite">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-3xl text-petrol"></i>
          <p className="text-sm text-navy-900/60 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-offwhite py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial Header */}
        <div className="max-w-3xl mb-16 space-y-4">
          <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {language === 'da' ? 'YDELSE · ERP ADVISORY' : language === 'en' ? 'SERVICE · ERP ADVISORY' : 'DIENSTLEISTUNG · ERP ADVISORY'}</span>
          <h1 className="text-4xl sm:text-5xl font-serif font-medium text-navy-900 leading-tight">
            {pageData ? getLocalizedValue(pageData.title, language) : t.home.erpTitle}
          </h1>
          <p className="text-lg text-navy-900/60 font-sans">
            {t.services.erpSubtitle}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column - Image & Quick Facts */}
          <div className="lg:col-span-5 space-y-8">
            <div className="relative rounded-none overflow-hidden shadow-2xl border border-bluegray">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8297297f-eccf-4eba-a924-6f973a63cdc4/erp-workshop.jpg"
                alt="ERP Workshop og Governance"
                className="w-full h-[350px] object-cover rounded-none"
              />
            </div>

            {/* Quick Facts Card */}
            <div className="bg-white rounded-none border border-bluegray p-6 sm:p-8 shadow-xl space-y-6">
              <h3 className="font-serif text-lg font-bold text-navy-900">{language === 'da' ? 'Hvorfor vælge René som ERP Lead?' : language === 'en' ? 'Why choose René as ERP Lead?' : 'Warum René als ERP Lead wählen?'}</h3>
              <ul className="space-y-4 text-sm text-charcoal/80">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-pet mt-0.5 shrink-0 text-petrol"></i>
                  <span><strong>{language === 'da' ? 'Uafhængig rådgivning:' : language === 'en' ? 'Independent advice:' : 'Unabhängige Beratung:'}</strong> {language === 'da' ? 'René er uafhængig af implementeringspartnere og varetager udelukkende jeres interesser.' : language === 'en' ? 'René is independent of implementation partners and solely represents your interests.' : 'René ist unabhängig von Implementierungspartnern und vertritt ausschließlich Ihre Interessen.'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-pet mt-0.5 shrink-0 text-petrol"></i>
                  <span><strong>{language === 'da' ? 'Dynamics 365 F&O ekspertise:' : language === 'en' ? 'Dynamics 365 F&O expertise:' : 'Dynamics 365 F&O Expertise:'}</strong> {language === 'da' ? 'Dybdegående kendskab til Microsofts ERP-økosystem.' : language === 'en' ? 'In-depth knowledge of Microsoft\'s ERP ecosystem.' : 'Tiefgehendes Wissen über das ERP-Ökosystem von Microsoft.'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-pet mt-0.5 shrink-0 text-petrol"></i>
                  <span><strong>{language === 'da' ? 'Brobygning:' : language === 'en' ? 'Bridging:' : 'Brückenbau:'}</strong> {language === 'da' ? 'Sikrer tæt alignment mellem forretningsledelsen, IT og eksterne leverandører.' : language === 'en' ? 'Ensures close alignment between business management, IT, and external suppliers.' : 'Sorgt für eine enge Abstimmung zwischen Geschäftsführung, IT und externen Lieferanten.'}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column - Detailed Text */}
          <div className="lg:col-span-7 space-y-8 bg-white rounded-none border border-bluegray p-8 sm:p-10 shadow-xl">
            <div 
              className="prose prose-navy max-w-none text-charcoal/80 leading-relaxed space-y-6 font-sans text-base"
              dangerouslySetInnerHTML={{ __html: pageData ? getLocalizedValue(pageData.content, language) : '' }}
            />

            {/* Call to Action */}
            <div className="pt-8 border-t border-bluegray/60 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h4 className="font-serif text-lg font-bold text-navy-900">{language === 'da' ? 'Har dit ERP-program brug for struktur?' : language === 'en' ? 'Does your ERP program need structure?' : 'Braucht Ihr ERP-Programm Struktur?'}</h4>
                <p className="text-sm text-charcoal/60 mt-1">{language === 'da' ? 'Lad os tage en uforpligtende snak om jeres udfordringer.' : language === 'en' ? 'Let\'s have a non-binding chat about your challenges.' : 'Lassen Sie uns unverbindlich über Ihre Herausforderungen sprechen.'}</p>
              </div>
              <Link
                to="/kontakt"
                className="bg-navy-900 hover:bg-petrol text-offwhite px-6 py-3.5 rounded-none text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-md whitespace-nowrap"
              >
                {t.nav.contact}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
